import log from "loglevel";

export const PAYMENT_POLL_INTERVALS_MS = [1000, 2000, 3000, 5000] as const;
export const PAYMENT_AWAIT_TIMEOUT_MS = 60000;

const terminalOrderStates = new Set([2, 898, 899, 999]);

export function isTerminalOrderState(state: number): boolean {
	return terminalOrderStates.has(state);
}

export interface AwaitPaymentOptions<TStatus extends { Status: number }> {
	fetchStatus: () => Promise<TStatus>;
	timeoutMs?: number;
	now?: () => number;
	signal?: AbortSignal;
}

function waitForNextPoll(delayMs: number, signal?: AbortSignal): Promise<boolean> {
	if (signal?.aborted === true) {
		return Promise.resolve(false);
	}

	return new Promise((resolve) => {
		const onAbort = () => {
			clearTimeout(timeoutId);
			resolve(false);
		};
		const timeoutId = setTimeout(() => {
			signal?.removeEventListener("abort", onAbort);
			resolve(true);
		}, delayMs);
		signal?.addEventListener("abort", onAbort, { once: true });
	});
}

export async function awaitPaymentSettled<TStatus extends { Status: number }>(
	options: AwaitPaymentOptions<TStatus>
): Promise<TStatus> {
	const timeoutMs = options.timeoutMs ?? PAYMENT_AWAIT_TIMEOUT_MS;
	const now = options.now ?? Date.now;
	const startedAt = now();
	let attempt = 0;
	// Первый запрос пробрасывает ошибку: без единого успешного ответа вернуть нечего.
	let last = await options.fetchStatus();

	while (!isTerminalOrderState(last.Status)) {
		const remainingMs = timeoutMs - (now() - startedAt);
		if (remainingMs <= 0 || options.signal?.aborted === true) {
			return last;
		}

		const intervalMs = PAYMENT_POLL_INTERVALS_MS[Math.min(attempt, PAYMENT_POLL_INTERVALS_MS.length - 1)];
		if (!(await waitForNextPoll(Math.min(intervalMs, remainingMs), options.signal))) {
			return last;
		}
		if (now() - startedAt >= timeoutMs) {
			return last;
		}

		attempt += 1;
		try {
			last = await options.fetchStatus();
		} catch (error) {
			// Сетевой сбой в середине ожидания не означает отказ платежа: держим последний
			// известный статус и продолжаем опрос до таймаута.
			log.warn("Payment status poll failed, keeping last known status", error);
		}
	}

	return last;
}
